import React, { useCallback, useEffect, useState } from "react";
import DashboardLayout from "../components/DashboardLayout";
import { api } from "../lib/apiClient";
import { toast } from "sonner";
import { Link, useSearchParams } from "react-router-dom";
import { CreditCard, CheckCircle2, Clock, RefreshCw } from "lucide-react";
import { useT } from "../lib/i18n";

const RS_STYLES = {
  REQUESTED: "bg-[#F59E0B]/10 text-[#F59E0B]",
  ACCEPTED_AWAITING_PAYMENT: "bg-[#0EA5E9]/10 text-[#0EA5E9]",
  IN_REVIEW: "bg-[#10B981]/10 text-[#10B981]",
  DELIVERED: "bg-[#10B981]/10 text-[#10B981]",
  COMPLETED: "bg-slate-100 text-[#475569]",
  DECLINED: "bg-slate-100 text-[#94A3B8]",
  EXPIRED: "bg-slate-100 text-[#94A3B8]",
  CANCELLED: "bg-[#EF4444]/10 text-[#EF4444]",
};

export const StatusBadge = ({ value }) => {
  const { t } = useT();
  return <span className={`px-2 py-0.5 rounded-full text-xs font-bold font-mono-kz ${RS_STYLES[value] || "bg-slate-100 text-[#475569]"}`}>{t(value)}</span>;
};

const eur = (cents) => `€${(cents / 100).toFixed(2)}`;

export default function ExpertDashboard() {
  const { t } = useT();
  const [params] = useSearchParams();
  const [connect, setConnect] = useState(null);
  const [requests, setRequests] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [c, r, e] = await Promise.allSettled([
      api.get("/consultants/connect/status"),
      api.get("/consultants/review-requests"),
      api.get("/consultants/earnings"),
    ]);
    if (c.status === "fulfilled") setConnect(c.value.data); else setConnect({ stripe_status: "UNCONFIGURED" });
    if (r.status === "fulfilled") setRequests(r.value.data.review_requests);
    if (e.status === "fulfilled") setEarnings(e.value.data);
  }, []);

  useEffect(() => { load(); }, [load, params]);

  const startOnboarding = async () => {
    setBusy(true);
    try {
      const r = await api.post("/consultants/connect/onboard", { origin_url: window.location.origin });
      window.location.href = r.data.onboarding_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Could not start onboarding"));
      setBusy(false);
    }
  };

  const act = async (id, action) => {
    setBusy(true);
    try {
      await api.post(`/review-requests/${id}/${action}`);
      toast.success(action === "accept" ? t("Request accepted — the customer will be asked to pay") : t("Request declined"));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const stripeReady = connect?.stripe_status === "READY";

  return (
    <DashboardLayout title={t("Expert dashboard")}
      action={<Link to="/become-expert" className="text-sm font-bold text-[#0EA5E9] hover:underline">{t("Edit profile")}</Link>}>
      <div className="max-w-4xl space-y-6">
        <div data-testid="payments-setup-card" className="bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <CreditCard className="w-5 h-5 text-[#0EA5E9]" />
              <div>
                <p className="font-display font-extrabold text-[#0B1B3D]">{t("Payments setup")}</p>
                <p className="text-sm text-[#475569]">
                  {connect?.stripe_status === "UNCONFIGURED" ? t("Marketplace payments are not enabled on this environment yet.")
                    : stripeReady ? t("Your Stripe account is ready to receive payments.")
                    : t("Complete the Stripe onboarding to receive paid review requests.")}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span data-testid="stripe-status-badge" className={`px-3 py-1 rounded-full text-xs font-bold font-mono-kz ${stripeReady ? "bg-[#10B981]/10 text-[#10B981]" : "bg-[#F59E0B]/10 text-[#F59E0B]"}`}>
                {connect?.stripe_status || "…"}
              </span>
              {connect && connect.stripe_status !== "UNCONFIGURED" && !stripeReady && (
                <button data-testid="connect-onboard-btn" disabled={busy} onClick={startOnboarding}
                  className="bg-[#0B1B3D] text-white hover:bg-[#1E3A8A] px-4 py-2 rounded-lg font-bold text-sm font-display disabled:opacity-50">
                  {connect.stripe_status === "NOT_STARTED" ? t("Set up payments") : t("Continue onboarding")}
                </button>
              )}
            </div>
          </div>
        </div>

        {earnings && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="earnings-summary">
            {[
              [t("Reviews sold"), earnings.summary.reviews_sold],
              [t("Gross"), eur(earnings.summary.gross_cents)],
              [t("Konzenzo fee"), eur(earnings.summary.platform_fee_cents)],
              [t("Your earnings"), eur(earnings.summary.net_cents)],
            ].map(([label, value]) => (
              <div key={label} className="bg-white border border-slate-200 rounded-xl p-4">
                <p className="font-mono-kz text-xs text-[#94A3B8] uppercase tracking-widest font-bold">{label}</p>
                <p className="font-display text-2xl font-black text-[#0B1B3D] mt-1">{value}</p>
              </div>
            ))}
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-display font-extrabold text-[#0B1B3D]">{t("Review requests")}</h2>
            <button onClick={load} className="text-sm font-bold text-[#475569] inline-flex items-center gap-1.5"><RefreshCw className="w-3.5 h-3.5" /> {t("Refresh")}</button>
          </div>
          {!requests || requests.length === 0 ? (
            <p className="p-6 text-sm text-[#94A3B8]" data-testid="requests-empty">{t("No review requests yet. Make sure your payments setup is complete and your availability is on.")}</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {requests.map((rr) => (
                <div key={rr.review_request_id} data-testid={`request-row-${rr.review_request_id}`} className="p-5 flex items-center gap-4 flex-wrap">
                  <div className="flex-1 min-w-[220px]">
                    <p className="font-bold text-[#0B1B3D]">{rr.session_title || t("Decision report")}</p>
                    <p className="text-xs text-[#94A3B8] font-mono-kz mt-0.5 flex items-center gap-2">
                      <Clock className="w-3 h-3" /> {rr.turnaround} · {new Date(rr.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-display font-black text-[#0B1B3D]">{eur(rr.service_amount_cents)}</p>
                    <p className="text-xs text-[#10B981] font-mono-kz">{t("You earn")} {eur(rr.consultant_amount_cents)}</p>
                  </div>
                  <StatusBadge value={rr.review_status} />
                  {rr.review_status === "REQUESTED" && (
                    <div className="flex gap-2">
                      <button data-testid={`accept-${rr.review_request_id}`} disabled={busy} onClick={() => act(rr.review_request_id, "accept")}
                        className="bg-[#10B981] text-white px-3 py-1.5 rounded-lg text-xs font-bold inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> {t("Accept")}</button>
                      <button data-testid={`decline-${rr.review_request_id}`} disabled={busy} onClick={() => act(rr.review_request_id, "decline")}
                        className="border border-slate-200 text-[#475569] px-3 py-1.5 rounded-lg text-xs font-bold">{t("Decline")}</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
